import { useRefCallback } from "@enfusion-ui/hooks";
import { NodeData, SelectOptionsType } from "@enfusion-ui/types";
import { getSelectOption } from "@enfusion-ui/utils";
import {
  Button,
  ErrorAlert,
  Modal,
  Select,
  TextInput,
} from "@enfusion-ui/web-components";
import { styled } from "@enfusion-ui/web-core";
import * as React from "react";

const ModalContentContainer = styled.div`
  margin-top: 1rem;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const InputWrapper = styled.div`
  width: 100%;
  min-width: 300px;
  margin: 5px 0px;
`;

const ButtonWrapper = styled.div`
  margin-top: 20px;
`;

type FileMoveFormProps = {
  currentFolder: string;
  onSubmit: (selectedNode: NodeData | null) => void;
  root?: string;
  nodes: NodeData[];
};

export type FileMoveModalProps = {
  open: boolean;
  filePath: string;
  onClose: VoidFunction;
  onSubmit: (selectedNode: NodeData | null) => void;
  root?: string;
  nodes: NodeData[];
};

const FileMoveForm: React.FC<FileMoveFormProps> = ({
  currentFolder,
  onSubmit,
  nodes,
}) => {
  const [selectedNode, setSelectedNode] = React.useState<NodeData | null>(null);
  const directories: SelectOptionsType<string>[] = [];
  nodes.forEach((folder: NodeData) => {
    if (!folder.file)
      directories.push({
        label: folder.name,
        value: JSON.stringify(folder),
      });
  });

  const handleSelectionChanged = useRefCallback(
    (e: { label: string; value: string } | null) => {
      if (e) {
        setSelectedNode(JSON.parse(e.value));
      }
    },
    []
  );

  const handleSubmit = () => {
    onSubmit(selectedNode);
  };

  return (
    <InputContainer>
      <InputWrapper>
        <TextInput
          name="currentFolder"
          label="Current Folder"
          value={currentFolder}
          autoFocus
          disabled
        />
      </InputWrapper>
      <InputWrapper>
        <Select
          options={directories}
          label="Choose Folder"
          value={getSelectOption(directories, JSON.stringify(selectedNode))}
          clearable={false}
          onChange={handleSelectionChanged}
        />
        <ButtonWrapper>
          <Button
            primary
            type="submit"
            onClick={handleSubmit}
            disabled={!selectedNode}
          >
            Submit
          </Button>
        </ButtonWrapper>
      </InputWrapper>
    </InputContainer>
  );
};

const FileMoveModal: React.FC<FileMoveModalProps> = (
  props: FileMoveModalProps
) => {
  const { open, onClose, filePath: fileName, onSubmit, root, nodes } = props;
  const [error, setError] = React.useState<Error | null>();

  const handleSubmit = (selectedNode: NodeData | null) => {
    try {
      onSubmit(selectedNode);
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setError(err as any);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Move File"
      allowDismissal
      content={
        <ModalContentContainer>
          {error && <ErrorAlert error={error} />}
          <FileMoveForm
            currentFolder={fileName}
            onSubmit={handleSubmit}
            root={root}
            nodes={nodes}
          />
        </ModalContentContainer>
      }
    />
  );
};

export default FileMoveModal;
